import { useState, useEffect, useCallback, useRef } from 'react';
import type { ComponentType } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useCapabilityDetail as useCapabilityDetailData } from '@/hooks/use-entity-detail';
import { useHeaderAction } from '@/lib/header-action-context';
import { useCapabilityActions } from '@/hooks/use-capability-actions';
import { api } from '@/lib/api-client';
import { CAPABILITY_TYPE_LABELS } from '@/types';
import { CapabilityHeaderActions } from '@/components/capability-detail/CapabilityHeaderActions';
import { Archive, Pencil } from 'lucide-react';
import { CapabilityDetailProvider } from '@/lib/capability-detail-context';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import WorkflowDetailPage from '@/pages/workflows/workflow-detail-page';
import AgentDetailPage from '@/pages/agents/agent-detail-page';
import ModelDetailPage from '@/pages/models/model-detail-page';
import PromptDetailPage from '@/pages/prompts/prompt-detail-page';
import ToolDetailPage from '@/pages/tools/tool-detail-page';
import SkillDetailPage from '@/pages/skills/skill-detail-page';
import RoleDetailPage from '@/pages/roles/role-detail-page';
import PolicyDetailPage from '@/pages/policies/policy-detail-page';
import DecisionDetailPage from '@/pages/decisions/decision-detail-page';
import SchemaDetailPage from '@/pages/schemas/schema-detail-page';
import SubscriptionDetailPage from '@/pages/subscriptions/subscription-detail-page';
import DomainTypeDetailPage from '@/pages/domain-types/domain-type-detail-page';
import PermissionDetailPage from '@/pages/permissions/permission-detail-page';
import ProviderDetailPage from '@/pages/providers/provider-detail-page';
import WorkerDetailPage from '@/pages/workers/worker-detail-page';
import ApplicationTypeDetailPage from '@/pages/capabilities/application-type-detail-page';
import MemoryDetailPage from '@/pages/memory/memory-detail-page';
import BindingDetailPage from '@/pages/bindings/binding-detail-page';
import IntegrationDetailPage from '@/pages/integrations/integration-detail-page';
import ConnectionDetailPage from '@/pages/connections/connection-detail-page';
import WorkspaceDetailPage from '@/pages/workspaces/workspace-detail-page';

const TYPE_TO_PAGE: Record<string, ComponentType<object>> = {
    workflow: WorkflowDetailPage,
    agent: AgentDetailPage,
    model: ModelDetailPage,
    prompt: PromptDetailPage,
    tool: ToolDetailPage,
    skill: SkillDetailPage,
    role: RoleDetailPage,
    memory: MemoryDetailPage,
    policy: PolicyDetailPage,
    decision: DecisionDetailPage,
    schema: SchemaDetailPage,
    subscription: SubscriptionDetailPage,
    'domain-type': DomainTypeDetailPage,
    permission: PermissionDetailPage,
    provider: ProviderDetailPage,
    worker: WorkerDetailPage,
    application: ApplicationTypeDetailPage,
    binding: BindingDetailPage,
    integration: IntegrationDetailPage,
    connection: ConnectionDetailPage,
    workspace: WorkspaceDetailPage,
};

const HEADER_LAYER_ID = 'capability-page';

function resolveTypePage(type?: string): ComponentType<object> | null {
    if (!type) return null;
    const lower = type.toLowerCase();
    const base = lower.replace(/[_-]type$/, '');
    const variants = [
        type,
        lower,
        lower.replace(/_/g, '-'),
        lower.replace(/-/g, '_'),
        base,
        base.replace(/_/g, '-'),
        base.replace(/-/g, '_'),
    ];
    for (const candidate of variants) {
        const page = TYPE_TO_PAGE[candidate];
        if (page) return page;
    }
    return null;
}

function CapabilityDetailPageInner({
    archiveDialogOpen,
    setArchiveDialogOpen,
}: {
    archiveDialogOpen: boolean;
    setArchiveDialogOpen: (open: boolean) => void;
}) {
    const { appKey, name } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data, isLoading, error, refetch } = useCapabilityDetailData(appKey!, name!);
    const { pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
    const [isArchiving, setIsArchiving] = useState(false);

    const type = data ? (data as { type?: string }).type : undefined;
    const typeLabel = type && CAPABILITY_TYPE_LABELS[type] ? CAPABILITY_TYPE_LABELS[type] : type ?? 'Capability';

    const actions = useCapabilityActions({
        appKey: appKey!,
        name: name!,
        typeLabel,
        data: data as Record<string, unknown> | null,
        refetch,
    });

    const saveRef = useRef(actions.save);
    saveRef.current = actions.save;
    const publishRef = useRef(actions.publish);
    publishRef.current = actions.publish;
    const editRef = useRef(actions.edit);
    editRef.current = actions.edit;

    useEffect(() => {
        if (type === 'agent' || type === 'model' || type === 'workflow' || type === 'integration') return;
        if (!isLoading && !error && data && !actions.isArchived) {
            pushHeaderLayer(HEADER_LAYER_ID, {
                action: (
                    <CapabilityHeaderActions
                        typeLabel={typeLabel}
                        hasWorkingRevision={actions.hasWorkingRevision}
                        isViewingPublished={actions.isViewingPublished}
                        isArchived={actions.isArchived}
                        selectedPublishedLabel={actions.selectedPublishedLabel}
                        isDirty={actions.isDirty}
                        isSaving={actions.isSaving}
                        isPublishing={actions.isPublishing}
                        onSave={() => void saveRef.current()}
                        onPublish={() => void publishRef.current()}
                        onEdit={() => editRef.current()}
                        onArchive={() => actions.openArchiveDialog()}
                        onSettings={() => actions.openSettings()}
                    />
                ),
            });
        } else if (type !== 'agent' && type !== 'model' && type !== 'workflow' && type !== 'integration') {
            removeHeaderLayer(HEADER_LAYER_ID);
        }
        return () => removeHeaderLayer(HEADER_LAYER_ID);
    }, [
        type,
        isLoading,
        error,
        data,
        actions.isArchived,
        actions.hasWorkingRevision,
        actions.isViewingPublished,
        actions.selectedPublishedLabel,
        actions.isDirty,
        actions.isSaving,
        actions.isPublishing,
        typeLabel,
        pushHeaderLayer,
        removeHeaderLayer,
    ]);

    if (isLoading) return <div className="p-6">Loading...</div>;
    if (error || !data) return <div className="p-6">Not found</div>;

    const capabilityName = (data as { name?: string }).name ?? name ?? '';
    const Page = resolveTypePage(type);

    if (!Page) {
        return (
            <div className="p-6">
                <p className="text-slate-600">Unknown capability type: {type ?? 'unknown'}</p>
            </div>
        );
    }

    async function handleArchive() {
        if (!appKey || !name) return;
        setIsArchiving(true);
        try {
            await api.delete(`/applications/${appKey}/capabilities/${name}`);
            toast.success(`${typeLabel} archived successfully`);
            queryClient.invalidateQueries({ queryKey: ['capabilities'] });
            navigate(`/applications/${appKey}/capabilities`, { replace: true });
        } catch {
            toast.error(`Failed to archive ${typeLabel.toLowerCase()}`);
        } finally {
            setIsArchiving(false);
            setArchiveDialogOpen(false);
        }
    }

    return (
        <>
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
                <div className="relative min-h-0 flex-1">
                    <div className="absolute inset-0 overflow-auto">
                        <Page />
                    </div>
                </div>
            </div>

            <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Archive size={18} className="text-destructive" />
                            Archive {typeLabel}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to archive &quot;{capabilityName}&quot;? It will no longer appear in the
                            active {typeLabel.toLowerCase()} list.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleArchive();
                            }}
                            disabled={isArchiving}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isArchiving ? 'Archiving...' : 'Archive'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={actions.replaceDialogOpen} onOpenChange={actions.setReplaceDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Pencil size={18} className="text-amber-600" />
                            Replace working copy?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            A working copy already exists. Replacing it will discard any unsaved changes
                            and create a new working copy based on the selected published version.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={actions.handleConfirmReplace}>
                            Replace
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

/**
 * Single capability detail route: /applications/:appKey/capabilities/:name
 * Fetches the capability by name (API is type-agnostic), then renders a
 * route-level title header (Back + Archive) and the appropriate type-specific
 * detail page. Archive is shown for all types from this single place.
 */
export default function CapabilityDetailPage() {
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const openArchiveDialog = useCallback(() => setArchiveDialogOpen(true), []);

    return (
        <CapabilityDetailProvider openArchiveDialog={openArchiveDialog}>
            <CapabilityDetailPageInner
                archiveDialogOpen={archiveDialogOpen}
                setArchiveDialogOpen={setArchiveDialogOpen}
            />
        </CapabilityDetailProvider>
    );
}
